/**
 * The Save bar every settings screen ends with.
 *
 * Position is a product rule, not a per-screen decision: **Save sits at the
 * bottom of the screen, aligned left**, everywhere. The top-right corner is
 * reserved for the action that creates a new item, so a Save button up there
 * competes with it and moves depending on which tab you are on — which is
 * exactly what the 2026-08-15 release review found ("кнопка «Сберегти
 * налаштування» на каждой вкладке в своём месте — какой-то абсурд").
 *
 * Screens differ in what they need to say next to the button (a dirty-state
 * warning, a secondary action), not in where the button goes — so those are
 * props here rather than a reason to hand-roll another bar.
 */

import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface SettingsSaveBarProps {
  onSave: () => void;
  /** In flight — the button shows a spinner and refuses further clicks. */
  isSaving?: boolean;
  /**
   * The form has hydrated from the server. Defaults to `true` for screens
   * with nothing to hydrate. Screens that persist every field from form
   * state must pass this: a Save fired before hydration is not a no-op, it
   * writes the empty form over the saved values.
   */
  isLoaded?: boolean;
  /**
   * The form's own contents are not currently saveable — a failed client-side
   * validation, say. Kept separate from `isLoaded` on purpose: the two look
   * identical to the button but mean opposite things to the user, and a
   * hydration affordance (skeleton, "loading settings…") must never appear
   * because someone mistyped a URL.
   */
  canSave?: boolean;
  /** Unsaved edits are pending — renders the "not saved yet" hint. */
  isDirty?: boolean;
  /** Overrides the default "Save changes" label. */
  label?: string;
  /** Rendered to the right of the button (e.g. a secondary action). */
  children?: React.ReactNode;
  buttonTestId?: string;
  className?: string;
}

export function SettingsSaveBar({
  onSave,
  isSaving = false,
  isLoaded = true,
  canSave = true,
  isDirty = false,
  label,
  children,
  buttonTestId = 'settings-save-button',
  className,
}: SettingsSaveBarProps) {
  const { t } = useTranslation(['common']);
  const disabled = isSaving || !isLoaded || !canSave;

  return (
    <div
      data-testid="settings-save-bar"
      className={cn(
        'mt-4 pt-4 border-t border-border flex flex-wrap items-center justify-start gap-3',
        className
      )}
    >
      <Button
        onClick={onSave}
        isLoading={isSaving}
        disabled={disabled}
        size="sm"
        data-testid={buttonTestId}
      >
        {!isSaving && <Save className="h-4 w-4 mr-2" />}
        {label ?? t('common:common.saveChanges', 'Save changes')}
      </Button>
      {isDirty && !isSaving && (
        <span className="text-xs text-warning" data-testid="settings-save-bar-dirty">
          {t('common:common.unsavedChanges', 'Unsaved changes')}
        </span>
      )}
      {children}
    </div>
  );
}
